<template>
  <v-container fluid class="data-import-view fill-height align-start pa-0" style="background-color: #F8F9FA;">
    <v-row no-gutters>
      <v-col cols="12">
        <div class="page-header pl-6 pt-3 pb-1">
          <h2 class="text-h6 font-weight-regular mb-1" style="color: #4A4A4A;">数据导入</h2>
          <div class="text-caption" style="color: #757575;">
            <v-icon x-small color="#4DB6AC">mdi-map-marker</v-icon>
            当前区域: {{ currentRegionDisplay }}
          </div>
        </div>
      </v-col>

      <v-col cols="12" class="px-6 py-0">
        <v-card flat color="transparent">
          <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-3">
            <v-tab class="font-weight-medium text-caption">文本数据</v-tab>
            <v-tab class="font-weight-medium text-caption">矢量数据</v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab" style="background-color: transparent;">
            <!-- Text Data Tab -->
            <v-tab-item>
              <div class="mb-3">
                <h3 class="text-subtitle-1 mb-2 font-weight-regular" style="color: #4A4A4A;">文本数据导入</h3>
                <v-card outlined class="upload-card d-flex align-center justify-center flex-column" height="200"
                  @dragover.prevent @drop.prevent="handleDrop($event, 'text')" :class="{ 'uploading': uploading }">
                  <v-progress-circular v-if="uploading" indeterminate color="#4DB6AC" size="48"></v-progress-circular>
                  <v-icon v-else size="48" color="#4DB6AC">mdi-cloud-upload-outline</v-icon>
                  <div class="text-subtitle-2 mt-3 mb-1">{{ uploading ? '上传中...' : '拖拽文件到此处或点击上传' }}
                  </div>
                  <div class="text-caption grey--text mb-3">支持 .xlsx, .xls, .csv 格式，文件 ≤ 100MB</div>
                  <v-btn small color="#4DB6AC" dark depressed @click="triggerFileInput('text')" :disabled="uploading">
                    选择文件
                  </v-btn>
                  <input type="file" ref="textFile" style="display: none" accept=".xlsx,.xls,.csv"
                    @change="handleFileSelect($event, 'text')">
                </v-card>
              </div>
            </v-tab-item>

            <!-- Vector Data Tab -->
            <v-tab-item>
              <div class="mb-3">
                <h3 class="text-subtitle-1 mb-2 font-weight-regular" style="color: #4A4A4A;">矢量数据导入</h3>
                <v-card outlined class="upload-card d-flex align-center justify-center flex-column" height="200"
                  @dragover.prevent @drop.prevent="handleDrop($event, 'vector')" :class="{ 'uploading': uploading }">
                  <v-progress-circular v-if="uploading" indeterminate color="#4DB6AC" size="48"></v-progress-circular>
                  <v-icon v-else size="48" color="#4DB6AC">mdi-cloud-upload-outline</v-icon>
                  <div class="text-subtitle-2 mt-3 mb-1">{{ uploading ? '上传中...' : '拖拽文件到此处或点击上传' }}
                  </div>
                  <div class="text-caption grey--text mb-3">支持 .zip (Shapefile), .gpkg, .geojson, .kml, .kmz 格式，文件
                    ≤ 100MB
                  </div>
                  <v-btn small color="#4DB6AC" dark depressed @click="triggerFileInput('vector')" :disabled="uploading">
                    选择文件
                  </v-btn>
                  <input type="file" ref="vectorFile" style="display: none" accept=".gpkg,.shp,.geojson,.kml,.kmz,.zip"
                    @change="handleFileSelect($event, 'vector')">
                </v-card>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-col>

      <!-- Import Log Section -->
      <v-col cols="12" class="px-6 mt-3">
        <div class="d-flex align-center justify-space-between mb-2">
          <h3 class="text-subtitle-1 font-weight-regular" style="color: #4A4A4A;">导入日志</h3>
          <div>
            <v-btn x-small text color="#4DB6AC" class="mr-1">
              <v-icon x-small left>mdi-download</v-icon> 导出
            </v-btn>
            <v-btn x-small text color="#4DB6AC" class="mr-1">
              <v-icon x-small left>mdi-delete-outline</v-icon> 清空
            </v-btn>
            <v-btn x-small text color="#4DB6AC">
              <v-icon x-small left>mdi-chevron-up</v-icon> 隐藏
            </v-btn>
          </div>
        </div>

        <v-data-table :headers="headers" :items="logs" class="elevation-0 log-table" :items-per-page="10" dense
          :loading="loadingLogs">
          <template v-slot:item.created_at="{ item }">
            <span class="text-caption">{{ formatDate(item.created_at) }}</span>
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip x-small :color="getStatusColor(item.status)" text-color="white">
              {{ item.status }}
            </v-chip>
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn x-small text color="#4DB6AC" @click="viewDetails(item)">
              查看
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Import Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="800px" scrollable>
      <v-card>
        <v-card-title class="text-h6 primary white--text py-3">
          <v-icon left color="white" size="20">mdi-file-document-outline</v-icon>
          导入详情
          <v-spacer></v-spacer>
          <v-btn icon small dark @click="detailsDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-3" v-if="selectedLog">
          <!-- Summary Alert -->
          <v-alert :type="getAlertType(selectedLog)" dense border="left" class="mb-3">
            <div class="d-flex align-center">
              <div class="flex-grow-1">
                <div class="text-subtitle-2 font-weight-bold">{{ selectedLog.file_name }}</div>
                <div class="text-caption mt-1">
                  {{ formatDate(selectedLog.created_at) }}
                  <span v-if="selectedLog.duration" class="ml-2">• 耗时 {{ selectedLog.duration
                  }}秒</span>
                </div>
              </div>
            </div>
          </v-alert>

          <!-- Statistics -->
          <v-row dense class="mb-1">
            <v-col cols="4">
              <v-card outlined class="text-center pa-2">
                <div class="text-h5 font-weight-bold">{{ selectedLog.total_rows }}</div>
                <div class="text-caption grey--text text--darken-1">总计</div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card outlined class="text-center pa-2">
                <div class="text-h5 font-weight-bold green--text">{{ selectedLog.success_count }}</div>
                <div class="text-caption grey--text text--darken-1">成功</div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card outlined class="text-center pa-2">
                <div class="text-h5 font-weight-bold red--text">{{ selectedLog.fail_count }}</div>
                <div class="text-caption grey--text text--darken-1">失败</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Error Details List -->
          <div v-if="selectedLog.error_details && selectedLog.error_details.length > 0">
            <v-divider class="my-2"></v-divider>
            <div class="d-flex align-center mb-2">
              <span class="text-subtitle-2 font-weight-bold">错误详情（共{{ selectedLog.error_details.length
              }}条）</span>
              <v-spacer></v-spacer>
              <v-btn x-small text color="primary" @click="copyErrors" class="text-caption">
                <v-icon x-small left>mdi-content-copy</v-icon>
                复制
              </v-btn>
            </div>

            <v-card outlined max-height="350" style="overflow-y: auto;" class="error-list">
              <v-list dense class="py-0">
                <v-list-item v-for="(err, idx) in selectedLog.error_details" :key="idx" class="px-2 py-1"
                  :class="idx % 2 === 0 ? 'grey lighten-5' : ''" style="min-height: 32px;">
                  <v-list-item-content class="py-0">
                    <v-list-item-title class="text-caption">
                      <v-chip x-small color="error" text-color="white" class="mr-1 px-2" style="height: 18px;">
                        第{{ err.row }}行
                      </v-chip>
                      <span class="text-body-2">{{ err.error }}</span>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </div>

          <!-- Processing Status -->
          <div v-else-if="selectedLog.total_rows === 0">
            <v-divider class="my-2"></v-divider>
            <v-alert dense type="info" border="left" class="mb-0">
              <div class="text-subtitle-2">正在处理中</div>
              <div class="text-caption mt-1">系统正在后台处理数据，请稍后刷新查看结果</div>
            </v-alert>
          </div>

          <!-- Success Status -->
          <div v-else>
            <v-divider class="my-2"></v-divider>
            <v-alert dense type="success" border="left" class="mb-0">
              <div class="text-subtitle-2">✓ 导入成功</div>
              <div class="text-caption mt-1">所有数据已成功导入</div>
            </v-alert>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-2 px-3">
          <v-spacer></v-spacer>
          <v-btn small text @click="detailsDialog = false">关闭</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <wgu-app-loading-mask></wgu-app-loading-mask>
  </v-container>
</template>

<script>
import AppLoadingMask from '../components/AppLoadingMask.vue'
import axios from '@/axios'

export default {
  name: 'DataImportView',
  components: {
    'wgu-app-loading-mask': AppLoadingMask
  },
  data () {
    return {
      activeTab: 1, // Default to Vector Data as per screenshot
      headers: [
        { text: '时间', value: 'created_at', sortable: true },
        { text: '操作类型', value: 'import_type', sortable: false },
        { text: '文件名', value: 'file_name', sortable: false },
        { text: '总行数', value: 'total_rows', sortable: false },
        { text: '成功', value: 'success_count', sortable: false },
        { text: '失败', value: 'fail_count', sortable: false },
        { text: '状态', value: 'status', sortable: false },
        { text: '操作', value: 'action', sortable: false }
      ],
      logs: [],
      uploading: false,
      loadingLogs: false,
      detailsDialog: false,
      selectedLog: null
    }
  },
  computed: {
    currentRegionDisplay () {
      const user = this.$store.state.user
      const userRole = user?.role?.role_code
      const selectedArea = this.$store.state.selectedArea

      // For INSURER role: only show bound region (they can't choose other regions)
      if (userRole === 'INSURER') {
        if (user?.bound_region_name) {
          return `${user.bound_region_name} (您的绑定区域)`
        }
        if (user?.bound_region_code) {
          return `${user.bound_region_code} (您的绑定区域)`
        }
        return '未绑定区域（请联系管理员）'
      }

      // For ADMIN and CLIENT roles: show selected area first, then bound region
      if (selectedArea?.display_name) {
        return selectedArea.display_name
      }
      if (user?.bound_region_name) {
        return `${user.bound_region_name} (默认)`
      }
      return '未选择（请使用顶部搜索框选择区域）'
    }
  },
  mounted () {
    this.loadImportLogs()
    // Auto-refresh logs every 5 seconds
    this.refreshInterval = setInterval(() => {
      this.loadImportLogs()
    }, 5000)
  },
  beforeDestroy () {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    triggerFileInput (type) {
      if (type === 'text') {
        this.$refs.textFile.click();
      } else {
        this.$refs.vectorFile.click();
      }
    },
    handleFileSelect (event, type) {
      const file = event.target.files[0];
      if (file) {
        this.uploadFile(file, type);
      }
      // Reset input to allow re-uploading same file
      event.target.value = ''
    },
    handleDrop (event, type) {
      const file = event.dataTransfer.files[0];
      if (file) {
        this.uploadFile(file, type);
      }
    },
    async uploadFile (file, type) {
      // Validate file size (100MB)
      const maxSize = 100 * 1024 * 1024
      if (file.size > maxSize) {
        this.$notify({
          title: '错误',
          text: '文件大小不能超过 100MB',
          type: 'error'
        })
        return
      }

      // Validate file extension
      const fileName = file.name.toLowerCase()
      if (type === 'text') {
        if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls') && !fileName.endsWith('.csv')) {
          this.$notify({
            title: '错误',
            text: '文本数据仅支持 Excel 或 CSV 格式（.xlsx, .xls, .csv）',
            type: 'error'
          })
          return
        }
      } else if (type === 'vector') {
        if (!fileName.endsWith('.zip') && !fileName.endsWith('.shp') && !fileName.endsWith('.geojson') && !fileName.endsWith('.kml') && !fileName.endsWith('.kmz') && !fileName.endsWith('.gpkg')) {
          this.$notify({
            title: '错误',
            text: '矢量数据仅支持 Shapefile、GeoPackage、GeoJSON 或 KML/KMZ 格式（.zip, .gpkg, .shp, .geojson, .kml, .kmz）',
            type: 'error'
          })
          return
        }
      }

      // Get region code from selected area or user's bound region
      // Backend logic: INSURER role can ONLY upload for their bound_region_code
      const selectedArea = this.$store.state.selectedArea
      const user = this.$store.state.user
      const userRole = user?.role?.role_code

      console.log('=== 数据导入区域检查 ===')
      console.log('selectedArea:', selectedArea)
      console.log('user:', user)
      console.log('userRole:', userRole)
      console.log('selectedArea?.place_id:', selectedArea?.place_id)
      console.log('user?.bound_region_code:', user?.bound_region_code)

      let regionCode

      // For INSURER role: must use bound_region_code (backend enforces this)
      if (userRole === 'INSURER') {
        regionCode = user?.bound_region_code
        if (!regionCode) {
          this.$notify({
            title: '错误',
            text: '您的账户未绑定区域，请联系管理员',
            type: 'error'
          })
          return
        }
        // If user selected a different region, warn them
        if (selectedArea?.place_id && selectedArea.place_id !== regionCode) {
          const warningMsg = `您选择的区域与绑定区域不一致，系统将使用您的绑定区域：${user.bound_region_name || regionCode}`
          console.warn('INSURER 选择区域不匹配:', warningMsg)
          this.$notify({
            title: '警告',
            text: warningMsg,
            type: 'warn'
          })
        }
      } else {
        // For ADMIN and CLIENT roles: prefer selectedArea, fallback to bound_region_code
        regionCode = selectedArea?.place_id || user?.bound_region_code
        if (!regionCode) {
          console.error('区域代码检查失败:', {
            userRole: userRole || '未知',
            hasSelectedArea: !!selectedArea,
            selectedAreaPlaceId: selectedArea?.place_id,
            boundRegionCode: user?.bound_region_code
          })
          this.$notify({
            title: '错误',
            text: '请先使用顶部搜索框选择区域',
            type: 'error'
          })
          return
        }
      }

      console.log('最终使用的 regionCode:', regionCode)

      // Prepare form data
      const formData = new FormData()
      formData.append('file', file)
      formData.append('import_type', type === 'text' ? 'policy' : 'plot')
      formData.append('region_code', regionCode)

      this.uploading = true

      try {
        console.log('=== 开始上传文件 ===')
        console.log('文件名:', file.name)
        console.log('导入类型:', type === 'text' ? 'policy' : 'plot')
        console.log('用户角色:', userRole)

        const response = await axios.post('/imports/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        console.log('=== 上传成功 ===')
        console.log('响应数据:', response.data)

        this.$notify({
          title: '成功',
          text: `${file.name} 上传成功，正在后台处理`,
          type: 'success'
        })

        // Refresh logs immediately
        await this.loadImportLogs()
      } catch (error) {
        console.error('=== 上传失败 ===')
        console.error('完整错误:', error)
        console.error('响应数据:', error.response?.data)
        console.error('状态码:', error.response?.status)

        let errorMsg = '上传失败'

        if (error.response?.status === 403) {
          errorMsg = error.response?.data?.detail || '权限不足，请检查您是否有权上传此区域的数据'
        } else if (error.response?.status === 400) {
          errorMsg = error.response?.data?.detail || '请求参数错误，请检查文件格式和区域选择'
        } else if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail
        } else {
          errorMsg = `网络错误：${error.message}`
        }

        this.$notify({
          title: '错误',
          text: errorMsg,
          type: 'error'
        })
      } finally {
        this.uploading = false
      }
    },
    async loadImportLogs () {
      if (this.loadingLogs) return

      this.loadingLogs = true
      try {
        const response = await axios.get('/imports/', {
          params: {
            skip: 0,
            limit: 20
          }
        })
        this.logs = response.data.map(log => ({
          ...log,
          status: this.getLogStatus(log),
          import_type: log.import_type === 'policy' ? '文本数据导入' : '矢量数据导入'
        }))
      } catch (error) {
        console.error('Load logs error:', error)
      } finally {
        this.loadingLogs = false
      }
    },
    getLogStatus (log) {
      if (log.total_rows === 0) {
        return '处理中'
      } else if (log.fail_count === 0 && log.success_count > 0) {
        return '成功'
      } else if (log.success_count === 0 && log.fail_count === 0) {
        return '失败' // Case where transaction rolled back or logic failed before updating counts
      } else if (log.success_count > 0) {
        return '部分成功'
      } else {
        return '失败'
      }
    },
    getStatusColor (status) {
      if (status === '成功') return '#A5D6A7'; // Light Green
      if (status === '部分成功') return '#FFE082'; // Amber Light
      if (status === '失败') return '#EF9A9A'; // Red Light
      if (status === '处理中') return '#90CAF9'; // Blue Light
      return 'grey';
    },
    viewDetails (item) {
      console.log('导入详情:', item)
      this.selectedLog = item
      this.detailsDialog = true
    },
    getAlertType (log) {
      if (log.total_rows === 0) return 'info'
      if (log.fail_count === 0) return 'success'
      if (log.success_count > 0) return 'warning'
      return 'error'
    },
    async copyErrors () {
      if (!this.selectedLog?.error_details) return

      const errorText = this.selectedLog.error_details
        .map(err => `第 ${err.row} 行: ${err.error}`)
        .join('\n')

      const fullText = `导入详情 - ${this.selectedLog.file_name}
文件名: ${this.selectedLog.file_name}
导入时间: ${this.formatDate(this.selectedLog.created_at)}
总计: ${this.selectedLog.total_rows} 条
成功: ${this.selectedLog.success_count} 条
失败: ${this.selectedLog.fail_count} 条

错误详情：
${errorText}
`

      try {
        await navigator.clipboard.writeText(fullText)
        this.$message?.success('错误信息已复制到剪贴板')
      } catch (err) {
        console.error('复制失败:', err)
        // Fallback to textarea method
        const textarea = document.createElement('textarea')
        textarea.value = fullText
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        this.$message?.success('错误信息已复制到剪贴板')
      }
    },
    formatDate (dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    }
  }
}
</script>

<style scoped>
.data-import-view {
  min-height: 100%;
}

.upload-card {
  border: 1px dashed #BDBDBD !important;
  transition: all 0.3s;
}

.upload-card:hover {
  border-color: #4DB6AC !important;
  background-color: #F0F4C3;
}

.upload-card.uploading {
  border-color: #4DB6AC !important;
  background-color: #E0F2F1;
  pointer-events: none;
}

.log-table>>>.v-data-table-header th {
  background-color: #E0F2F1 !important;
  color: #00695C !important;
  font-weight: bold;
  font-size: 0.8rem !important;
  padding: 8px 12px !important;
  height: 36px !important;
}

.log-table>>>.v-data-table__wrapper tbody tr td {
  padding: 6px 12px !important;
  font-size: 0.8rem !important;
  height: 40px !important;
}

.limit-width {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

/* Error list styling */
.error-list {
  background-color: #fafafa;
}

.error-list .v-list-item {
  border-bottom: 1px solid #e0e0e0;
}

.error-list .v-list-item:last-child {
  border-bottom: none;
}

/* Beautify scrollbar */
.error-list::-webkit-scrollbar {
  width: 6px;
}

.error-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.error-list::-webkit-scrollbar-thumb {
  background: #bdbdbd;
  border-radius: 3px;
}

.error-list::-webkit-scrollbar-thumb:hover {
  background: #9e9e9e;
}

/* Compact spacing for dialog */
.v-dialog .v-card__text {
  line-height: 1.3;
}

.v-dialog .v-alert {
  padding: 8px 12px;
}

.v-dialog .v-chip {
  font-size: 11px;
}

/* Compact page header */
.page-header {
  line-height: 1.2;
}
</style>
